/**
 * Welcome Packet — single source of truth for per-class onboarding information.
 *
 * Content lives in `public.class_welcome_packets` (one row per class, plus an
 * optional row per session for overrides). Resolution priority is enforced in
 * the database function `resolve_welcome_packet`:
 *
 *      session override  ->  class welcome packet  ->  nothing
 *
 * Nothing here is hardcoded per class, and no email template may carry its own
 * copy of these instructions.
 */

import { supabase } from "@/integrations/supabase/client";

export interface WelcomePacketSection {
  key: string;
  heading: string;
  content: string;
  enabled: boolean;
  linkUrl?: string;
  linkLabel?: string;
}

export interface WelcomePacketSectionDef {
  key: string;
  label: string;
  defaultHeading: string;
  placeholder: string;
}

/** The catalog of sections an administrator may fill in. Order = email order. */
export const WELCOME_PACKET_SECTIONS: WelcomePacketSectionDef[] = [
  {
    key: "welcome_message",
    label: "Welcome message",
    defaultHeading: "Welcome!",
    placeholder: "A short, warm note from you or the instructor.",
  },
  {
    key: "what_to_wear",
    label: "What to wear",
    defaultHeading: "What to Wear",
    placeholder: "Athletic clothing and closed-toe athletic shoes.",
  },
  {
    key: "what_to_bring",
    label: "What to bring",
    defaultHeading: "What to Bring",
    placeholder: "A labeled water bottle.",
  },
  {
    key: "required_supplies",
    label: "Required supplies",
    defaultHeading: "Required Supplies",
    placeholder: "Items families must provide.",
  },
  {
    key: "optional_supplies",
    label: "Optional supplies",
    defaultHeading: "Optional Supplies",
    placeholder: "Nice-to-have items.",
  },
  {
    key: "location",
    label: "Exact location instructions",
    defaultHeading: "Location & Arrival",
    placeholder: "Building, room, entrance and check-in details.",
  },
  {
    key: "parking",
    label: "Parking / drop-off",
    defaultHeading: "Parking & Drop-Off",
    placeholder: "Where to park and how drop-off works.",
  },
  {
    key: "parent_participation",
    label: "Parent participation",
    defaultHeading: "Parent Participation",
    placeholder: "Whether a parent stays, helps, or drops off.",
  },
  {
    key: "food_allergy",
    label: "Food / allergy notes",
    defaultHeading: "Food & Allergies",
    placeholder: "Ingredients used, allergy reporting instructions.",
  },
  {
    key: "safety",
    label: "Safety notes",
    defaultHeading: "Safety",
    placeholder: "Equipment, supervision, and safety expectations.",
  },
  {
    key: "instructor_contact",
    label: "Instructor contact instructions",
    defaultHeading: "Contacting Your Instructor",
    placeholder: "How and when families should reach the instructor.",
  },
  {
    key: "communication_group",
    label: "BAND / communication group",
    defaultHeading: "Class Communication Group",
    placeholder: "Explain the group and add the invite link below.",
  },
  {
    key: "first_day",
    label: "First-day instructions",
    defaultHeading: "Your First Day",
    placeholder: "What happens at the first meeting.",
  },
  {
    key: "cancellation_policy",
    label: "Cancellation / makeup policy",
    defaultHeading: "Cancellations & Makeups",
    placeholder: "What happens if a class is missed or cancelled.",
  },
  {
    key: "additional_notes",
    label: "Additional notes",
    defaultHeading: "Additional Notes",
    placeholder: "Anything else families should know.",
  },
];

export const SECTION_DEF_BY_KEY: Record<string, WelcomePacketSectionDef> =
  Object.fromEntries(WELCOME_PACKET_SECTIONS.map((s) => [s.key, s]));

/** Strip the `db:` prefix used by some class identifiers. */
export function bareClassSlug(slug: string): string {
  return String(slug || "").replace(/^db:/, "");
}

/** A blank editable packet (all sections off) used when nothing is saved yet. */
export function emptyPacketSections(): WelcomePacketSection[] {
  return WELCOME_PACKET_SECTIONS.map((def) => ({
    key: def.key,
    heading: def.defaultHeading,
    content: "",
    enabled: false,
    linkUrl: "",
    linkLabel: "",
  }));
}

/** Merge stored rows into the full editable list, preserving saved values. */
export function toEditableSections(stored: unknown): WelcomePacketSection[] {
  const rows: WelcomePacketSection[] = Array.isArray(stored)
    ? (stored as WelcomePacketSection[])
    : [];
  const byKey = new Map(rows.map((r) => [r.key, r]));
  const base = WELCOME_PACKET_SECTIONS.map((def) => {
    const found = byKey.get(def.key);
    return {
      key: def.key,
      heading: found?.heading?.trim() || def.defaultHeading,
      content: found?.content ?? "",
      enabled: !!found?.enabled,
      linkUrl: found?.linkUrl ?? "",
      linkLabel: found?.linkLabel ?? "",
    } satisfies WelcomePacketSection;
  });
  // Preserve any custom sections that aren't in the catalog.
  for (const row of rows) {
    if (!SECTION_DEF_BY_KEY[row.key]) {
      base.push({
        key: row.key,
        heading: row.heading || row.key,
        content: row.content ?? "",
        enabled: !!row.enabled,
        linkUrl: row.linkUrl ?? "",
        linkLabel: row.linkLabel ?? "",
      });
    }
  }
  return base;
}

/** Blank or disabled sections never leave the editor. */
export function packetSectionsForDelivery(
  sections: WelcomePacketSection[],
): WelcomePacketSection[] {
  return sections.filter(
    (s) => s.enabled && ((s.content ?? "").trim() !== "" || (s.linkUrl ?? "").trim() !== ""),
  );
}

/**
 * Delivery readiness of a packet. A packet only ever emails families when it
 * is `ready`; publishing a class never changes this.
 */
export type WelcomePacketStatus = "draft" | "ready" | "paused";

export const WELCOME_PACKET_STATUS_LABEL: Record<WelcomePacketStatus, string> = {
  draft: "Draft — never sent to families",
  ready: "Ready — sends with new registrations",
  paused: "Paused — temporarily not sending",
};

/** Load the raw editable row for a class (or one of its session overrides). */
export async function loadPacketRow(
  classSlug: string,
  sessionLabel: string | null,
): Promise<{
  id: string | null;
  sections: WelcomePacketSection[];
  status: WelcomePacketStatus;
  statusChangedAt: string | null;
  statusChangedByEmail: string | null;
}> {
  const slug = bareClassSlug(classSlug);
  let query = (supabase as any)
    .from("class_welcome_packets")
    .select("id, sections, status, status_changed_at")
    .eq("class_slug", slug);
  query = sessionLabel ? query.eq("session_label", sessionLabel) : query.is("session_label", null);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  const status = (data?.status as WelcomePacketStatus) || "draft";
  // Internal staff email is admin-only and never exposed through the table API.
  let statusChangedByEmail: string | null = null;
  if (data?.id) {
    const { data: emailData } = await (supabase as any).rpc(
      "admin_welcome_packet_status_email",
      { _packet_id: data.id },
    );
    statusChangedByEmail = (emailData as string) ?? null;
  }
  return {
    id: (data?.id as string) ?? null,
    sections: toEditableSections(data?.sections),
    status: ["draft", "ready", "paused"].includes(status) ? status : "draft",
    statusChangedAt: (data?.status_changed_at as string) ?? null,
    statusChangedByEmail,
  };
}


/** Set Draft / Ready / Paused for one packet scope. */
export async function setPacketStatus(
  classSlug: string,
  sessionLabel: string | null,
  status: WelcomePacketStatus,
): Promise<void> {
  const slug = bareClassSlug(classSlug);
  const { data: auth } = await supabase.auth.getUser();
  const patch = {
    status,
    status_changed_at: new Date().toISOString(),
    status_changed_by_email: auth?.user?.email ?? null,
  };
  const existing = await loadPacketRowId(slug, sessionLabel);
  if (existing) {
    const { error } = await (supabase as any)
      .from("class_welcome_packets")
      .update(patch)
      .eq("id", existing);
    if (error) throw error;
  } else {
    const { error } = await (supabase as any)
      .from("class_welcome_packets")
      .insert({ class_slug: slug, session_label: sessionLabel, sections: [], ...patch });
    if (error) throw error;
  }
}

export async function savePacketRow(
  classSlug: string,
  sessionLabel: string | null,
  sections: WelcomePacketSection[],
): Promise<void> {
  const slug = bareClassSlug(classSlug);
  if (!slug) throw new Error("This class has no slug, so its packet can't be saved.");
  const payload = sections.map((s) => ({
    key: s.key,
    heading: s.heading?.trim() || SECTION_DEF_BY_KEY[s.key]?.defaultHeading || s.key,
    content: s.content ?? "",
    enabled: !!s.enabled,
    linkUrl: (s.linkUrl ?? "").trim(),
    linkLabel: (s.linkLabel ?? "").trim(),
  }));

  const existing = await loadPacketRowId(slug, sessionLabel);
  console.info("[welcome-packet] saving", {
    slug,
    sessionLabel,
    rowId: existing,
    sections: payload.length,
  });

  let rowId = existing;
  if (existing) {
    const { data, error } = await supabase
      .from("class_welcome_packets")
      .update({ sections: payload })
      .eq("id", existing)
      .select("id");
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error(
        "The packet did not save — your account may not have permission to edit welcome packets.",
      );
    }
  } else {
    const { data, error } = await supabase
      .from("class_welcome_packets")
      .insert({ class_slug: slug, session_label: sessionLabel, sections: payload })
      .select("id");
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error(
        "The packet did not save — your account may not have permission to edit welcome packets.",
      );
    }
    rowId = (data[0]?.id as string) ?? null;
  }

  // Read the row back and prove the values landed. A silent no-op write (RLS,
  // stale row id, wrong scope) must never look like a success.
  const { data: verify, error: verifyErr } = await supabase
    .from("class_welcome_packets")
    .select("id, sections, updated_at")
    .eq("id", rowId as string)
    .maybeSingle();
  if (verifyErr) throw verifyErr;
  const saved = Array.isArray(verify?.sections) ? (verify.sections as unknown[]) : [];
  // `sections` is jsonb. Postgres normalizes object-key order, so comparing raw
  // JSON strings reports a false failure even after a successful write. Compare
  // recursively sorted values instead while preserving array order.
  const canonicalJson = (value: unknown): string => {
    const normalize = (item: unknown): unknown => {
      if (Array.isArray(item)) return item.map(normalize);
      if (item && typeof item === "object") {
        return Object.fromEntries(
          Object.entries(item as Record<string, unknown>)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([key, child]) => [key, normalize(child)]),
        );
      }
      return item;
    };
    return JSON.stringify(normalize(value));
  };
  if (canonicalJson(saved) !== canonicalJson(payload)) {
    console.error("[welcome-packet] verification mismatch", { rowId, saved, payload });
    throw new Error(
      "The packet was written but the database returned different content. Nothing was kept — please try again.",
    );
  }
  console.info("[welcome-packet] saved", { rowId, updatedAt: verify?.updated_at });
}

async function loadPacketRowId(slug: string, sessionLabel: string | null) {
  let query = supabase.from("class_welcome_packets").select("id").eq("class_slug", slug);
  query = sessionLabel ? query.eq("session_label", sessionLabel) : query.is("session_label", null);
  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  return (data?.id as string) ?? null;
}

/**
 * Resolved, delivery-ready packet. Same function the email pipeline uses, so
 * the roster, parent page and emails can never drift apart.
 */
export async function resolveWelcomePacket(
  classSlug: string,
  sessionLabel?: string | null,
): Promise<WelcomePacketSection[]> {
  const { data, error } = await supabase.rpc("resolve_welcome_packet", {
    p_class_slug: bareClassSlug(classSlug),
    p_session_label: sessionLabel ?? null,
  });
  if (error) {
    console.error("resolveWelcomePacket failed", error);
    return [];
  }
  return Array.isArray(data) ? (data as unknown as WelcomePacketSection[]) : [];
}

/** Which session override rows exist for a class. */
export async function listPacketSessionOverrides(classSlug: string): Promise<string[]> {
  const { data } = await supabase
    .from("class_welcome_packets")
    .select("session_label")
    .eq("class_slug", bareClassSlug(classSlug))
    .not("session_label", "is", null);
  return (data ?? []).map((r: { session_label: string }) => r.session_label).filter(Boolean);
}
